import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, XCircle } from 'lucide-react';
import { verifyMagicLink, getErrorCode } from '@/services/api';
import { useBrand } from '@/contexts/BrandContext';
import { popReturnUrl } from '@/utils';
import { Button, ErrorState } from '@airclou/ui';

type VerifyState = 'verifying' | 'success' | 'error';

export function VerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refetchSession } = useBrand();

  const token = searchParams.get('token');
  const [state, setState] = useState<VerifyState>('verifying');
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const verifyAttempted = useRef(false);

  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorCode('INVALID_TOKEN');
      return;
    }

    // Guard against double execution (React StrictMode or unstable deps)
    if (verifyAttempted.current) return;
    verifyAttempted.current = true;

    const verify = async () => {
      try {
        await verifyMagicLink(token);
        setState('success');
        refetchSession();

        // Redirect after successful verification
        const returnUrl = popReturnUrl();
        setTimeout(() => {
          navigate(returnUrl, { replace: true });
        }, 1500);
      } catch (error) {
        setState('error');
        setErrorCode(getErrorCode(error));
      }
    };

    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (state === 'verifying') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Verifying your sign-in link...</p>
        </div>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-4">
            <svg
              className="h-8 w-8 text-green-600 dark:text-green-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Signed in
          </h1>
          <p className="text-muted-foreground">
            Redirecting you back...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  const getErrorMessage = () => {
    switch (errorCode) {
      case 'INVALID_TOKEN':
        return 'This sign-in link is invalid.';
      case 'TOKEN_EXPIRED':
        return 'This sign-in link has expired.';
      case 'TOKEN_USED':
        return 'This sign-in link has already been used.';
      default:
        return 'Failed to verify your sign-in link.';
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
          <XCircle className="h-8 w-8 text-destructive" />
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2">
          Sign-in failed
        </h1>
        <p className="text-muted-foreground mb-6">
          {getErrorMessage()}
        </p>
        <div className="space-y-3">
          <Button onClick={() => navigate('/auth/login')} className="w-full">
            Request a new link
          </Button>
          <Link
            to="/"
            className="block text-sm text-muted-foreground hover:text-foreground"
          >
            Back to Help Center
          </Link>
        </div>
      </div>
    </div>
  );
}
